// Ekran 01 — tam sayfa kart görünümü.
// ref_files/btys-erp-01-stok-karti.html içindeki fullHTML()/pr()/derin()
// fonksiyonlarının birebir karşılığı.
//
// İki varyant var:
//   · hammadde  -> .d-grid > (Genel Bilgiler + Değer Önceliği + Biyogaz Parametreleri) | yan kart
//   · diğer tür -> daha kısa form (Genel Bilgiler + Son Hareketler)
// Ayrıca "yeni kart" hali: r yok, tüm alanlar boş, Literatür kolonu "—".

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { useDrawer } from '../../components/Drawer'
import { ApiHatasi } from '../../lib/api'
import { sayiyaCevirBos, tr, trGiris } from '../../lib/trFormat'
import { birimSecenekleri, useBirimler } from '../../lib/birimler'
import { stokKartlari, type DegerCift, type StokKarti, type StokTuru } from './api'
import { HizliCariDrawer, HizliKategoriDrawer, KopyaDrawer } from './StokKartiDrawerlari'

export const TUR_ADI: Record<StokTuru, string> = {
  hammadde: 'Hammadde',
  urun: 'Ürün',
  sarf: 'Sarf Malzeme',
  yedek: 'Yedek Parça',
  yakit: 'Yakıt',
}

export const GRUPLAR: Record<StokTuru, string[]> = {
  hammadde: [
    'Hayvansal Gübreler',
    'Bitkisel / Silajlar',
    'Gıda ve İçecek Sanayi',
    'Mezbaha ve Hayvansal Yan Ürünler',
    'Arıtma Çamurları ve Belediye',
    'Diğer / Endüstriyel Yan Ürünler',
  ],
  urun: ['Fermente Gübre'],
  sarf: ['Gaz Temizleme', 'Yağlar & Kimyasallar', 'Proses Katkıları', 'Laboratuvar'],
  yedek: ['Kojenerasyon', 'Pompa & Karıştırıcı', 'Enstrümantasyon', 'Genel Mekanik'],
  yakit: ['Filo'],
}

const TEDARIKCILER = ['Endüstri Tedarik Ltd.', 'Akaryakıt A.Ş.', 'Kimya Market San.']

/** Referans `dv()`: boş değer dürüstçe "—" kalır. */
const dv = (v: number | string | null | undefined, basamak = 1) =>
  v === null || v === undefined || v === '' ? '—' : typeof v === 'number' ? tr(v, basamak) : v

// --------------------------------------------------------- değer önceliği

/**
 * Referans `pr()`: Parametre · Literatür · Tesis · Etkin Değer · Birim.
 * Etkin hücre kaynağı rozetler; tesis değeri varsa `.eff.tesis` sınıfını alır.
 */
function OncelikSatiri({
  etiket,
  literatur,
  tesis,
  etkin,
  birim,
  yeni,
  onTesisDegisti,
}: {
  etiket: string
  literatur: number | null
  tesis: number | null
  etkin: DegerCift | null
  birim: string
  yeni: boolean
  onTesisDegisti: (deger: string) => void
}) {
  return (
    <tr>
      <td>{etiket}</td>
      <td className="num">{yeni ? '—' : dv(literatur, 2)}</td>
      <td>
        <input
          className="pin"
          placeholder="—"
          // trGiris: gidiş dönüşü kayıpsız. tr(v,2) kullanılırken alana
          // girip çıkmak 3. ondalığı (numeric(12,3)) sessizce yuvarlıyordu.
          defaultValue={trGiris(tesis)}
          onBlur={(e) => onTesisDegisti(e.target.value)}
        />
      </td>
      <td>
        {!etkin || etkin.deger === null ? (
          <span style={{ color: '#8A98AB' }}>—</span>
        ) : (
          <span className={etkin.kaynak === 'tesis' ? 'eff tesis' : 'eff'}>
            {tr(etkin.deger, 2)}
            <span className="eff-src">
              {etkin.kaynak === 'tesis' ? 'Tesis' : etkin.kaynak === 'analiz' ? 'Analiz' : 'Literatür'}
            </span>
          </span>
        )}
      </td>
      <td className="num" style={{ color: '#8A98AB' }}>
        {birim}
      </td>
    </tr>
  )
}

// ------------------------------------------------- biyogaz parametreleri

/** Referans `derin()` — LfL/FNR şeması, iki sütunlu parametre tabloları. */
function BiyogazParametreleri({ kart, yeni }: { kart: StokKarti | null; yeni: boolean }) {
  const d = yeni ? null : kart
  const guven =
    d?.veriGuven === 'olcum'
      ? { s: '●', m: 'Birincil ölçüm', c: 'guv-o' }
      : d?.veriGuven === 'kurum'
        ? { s: '◐', m: 'Kurumsal referans / türetilmiş', c: 'guv-k' }
        : d?.veriGuven === 'celisik'
          ? { s: '⚠', m: 'Kaynaklar çelişiyor', c: 'guv-c' }
          : null

  const satir = (etiket: string, deger: string, birim = '') => (
    <tr key={etiket}>
      <td>{etiket}</td>
      <td className="num" style={{ fontWeight: 600 }}>{deger}</td>
      <td className="num" style={{ color: '#8A98AB', width: 86 }}>{birim}</td>
    </tr>
  )

  return (
    <div className="card" style={{ marginTop: 10 }}>
      <div className="sect-t">
        Biyogaz Parametreleri
        {guven ? (
          <span className={`guv ${guven.c}`} style={{ marginLeft: 8 }}>
            {guven.s} {guven.m}
          </span>
        ) : (
          <span className="guv guv-k" style={{ marginLeft: 8 }}>— kayıt yok</span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
        <table className="ptab">
          <thead><tr><th colSpan={3}>Fiziko-kimya &amp; Besin Profili</th></tr></thead>
          <tbody>
            {satir('Yoğunluk', dv(d?.yogunluk, 0), 'kg/m³')}
            {satir('pH', dv(d?.ph))}
            {/* Referans notu: C/N için LfL yayın yapmaz — boşluk dürüstçe korunur. */}
            <tr>
              <td>C/N oranı</td>
              <td className="num" style={{ fontWeight: 600 }}>
                {d?.cnOrani == null ? (
                  <>— <span style={{ color: '#8A98AB', fontSize: 10 }}>(LfL yayınlamaz)</span></>
                ) : (
                  dv(d.cnOrani)
                )}
              </td>
              <td className="num" style={{ color: '#8A98AB', width: 86 }} />
            </tr>
            {satir('Toplam azot (TKN)', dv(d?.toplamAzotTkn), 'kg/t KM')}
            {satir('Amonyum azotu (NH₄-N)', dv(d?.amonyumAzotu), 'kg/t KM')}
            {satir('Fosfor (P₂O₅)', dv(d?.fosforP2o5), 'kg/t KM')}
            {satir('Potasyum (K₂O)', dv(d?.potasyumK2o), 'kg/t KM')}
            {satir('Kükürt (S)', dv(d?.kukurtSKm, 2), '% KM')}
          </tbody>
        </table>

        <table className="ptab">
          <thead><tr><th colSpan={3}>Kompozisyon &amp; Kinetik &amp; İşletme</th></tr></thead>
          <tbody>
            {satir('Ham yağ', dv(d?.yagKm), '% KM')}
            {satir('Ham protein', dv(d?.proteinKm), '% KM')}
            {satir('Lignin', dv(d?.ligninKm), '% KM')}
            {satir('Ölçülen metan verimi (BMP)', dv(d?.metanVerimiBmp, 0), 'Nm³ CH₄/t oKM')}
            {satir('Org. madde bozunma oranı', dv(d?.bozunmaOrani, 0), '%')}
            {satir('Önerilen min. bekleme', dv(d?.minBeklemeGun, 0), 'gün')}
            {satir('Maks. karışım oranı', dv(d?.maksBeslemeOraniOkm, 0), '% oKM')}
            {satir('Mono-beslenebilir', d ? (d.monoBeslenebilir ? 'Evet' : 'Hayır') : '—')}
            {satir('Ön işlem', dv(d?.onIslem))}
            {satir('İnhibisyon riski', dv(d?.inhibisyonRiski))}
          </tbody>
        </table>
      </div>

      <table className="ptab">
        <thead>
          <tr>
            <th>ABP Kategorisi</th>
            <th>Atık Kodu (EWC)</th>
            <th>Mevsimsellik</th>
            <th>Depolama</th>
            <th className="num">Ömür (gün)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{dv(d?.abpKategorisi)}</td>
            <td className="mono" style={{ fontSize: 11 }}>{dv(d?.atikKoduEwc)}</td>
            <td>{dv(d?.mevsimsellik)}</td>
            <td>{dv(d?.depolamaSekli)}</td>
            <td className="num">{dv(d?.depolamaOmruGun, 0)}</td>
          </tr>
        </tbody>
      </table>

      <div className="pnote">
        Veri kaynağı: <b>{d?.veriKaynagi ?? '—'}</b> · Metan hesabı önceliği:{' '}
        <b>ölçülen BMP → verim × CH₄%</b> · Boş alanlar dürüstçe boş bırakılır — yakın
        substrat kopyalanmaz (LfL çıkarımı, bölüm 6).
      </div>
    </div>
  )
}

// -------------------------------------------------------------- tam sayfa

export default function TamSayfaKart({
  kod,
  yeniTur,
}: {
  /** null ise "Yeni Stok Kartı" hali. */
  kod: string | null
  yeniTur?: StokTuru
}) {
  const yeni = kod === null
  const { ac } = useDrawer()
  const kuyruk = useQueryClient()

  /**
   * Düzenlenebilir alanların yerel taslağı.
   *
   * Bu form referanstan `defaultValue` ile taşınmıştı ve Kaydet düğmesi YALNIZ
   * toast basıyordu: kullanıcı ad/grup/birim/kritik seviyeyi değiştirip
   * "kaydedildi" görüyor, hiçbir şey yazılmıyordu — sessiz veri kaybı.
   * Alanlar artık kontrollü ve düğme gerçek uca gidiyor.
   */
  const [taslak, setTaslak] = useState<{ ad: string; grup: string; birim: string; kritik: string }>({
    ad: '', grup: '', birim: '', kritik: '',
  })

  /**
   * Hızlı kayıtla eklenen kategoriler. §5.1'de `stok_karti.grup` serbest metin
   * kolonu; kategori kütüphanesi tablosu yok, bu yüzden yeni ad kart kaydedilene
   * kadar yalnız bu formun seçenek listesinde yaşıyor (referans da GRUPLAR
   * dizisine böyle ekliyordu).
   */
  const [ekKategoriler, setEkKategoriler] = useState<string[]>([])

  const { data: kart } = useQuery({
    queryKey: ['kart', kod],
    queryFn: () => stokKartlari.detay(kod!),
    enabled: !yeni,
  })

  const { data: teorik } = useQuery({
    queryKey: ['teorik', kod],
    queryFn: () => stokKartlari.teorik(kod!),
    enabled: !yeni && kart?.tur === 'hammadde',
  })

  const { data: sonrakiKod } = useQuery({
    queryKey: ['sonraki-kod', yeniTur ?? 'hammadde'],
    queryFn: () => stokKartlari.sonrakiKod(yeniTur ?? 'hammadde'),
    enabled: yeni,
  })

  // Kart yüklenince/değişince taslağı tazele.
  useEffect(() => {
    if (!kart) return
    setTaslak({
      ad: kart.ad,
      grup: kart.grup ?? '',
      birim: kart.birim ?? '',
      kritik: trGiris(kart.kritikSeviye),
    })
  }, [kart])

  const kaydet = useMutation({
    mutationFn: () =>
      stokKartlari.kaydet(kart!.id, {
        // Sunucu 27 alanın tamamını bekliyor; mevcut kartın üzerine yalnız
        // düzenlenenler yazılıyor.
        ...kart!,
        ad: taslak.ad.trim(),
        grup: taslak.grup || null,
        birim: taslak.birim.trim(),
        kritikSeviye: sayiyaCevirBos(taslak.kritik),
      }),
    onSuccess: async (yeniKart) => {
      kuyruk.setQueryData(['kart', kod], yeniKart)
      await Promise.all([
        kuyruk.invalidateQueries({ queryKey: ['kartlar'] }),
        kuyruk.invalidateQueries({ queryKey: ['teorik', kod] }),
      ])
      toast(`${yeniKart.kod} kaydedildi — ${yeniKart.ad}`)
    },
    onError: (h) =>
      toast(h instanceof ApiHatasi ? h.message : 'Stok kartı kaydedilemedi.'),
  })

  // NOT: aşağıdaki erken return'den ÖNCE çağrılmalı — kart yüklendiğinde
  // çağrılan kanca sayısı değişirse React render'ı düşürür. Sözlük dokuz
  // satır, tür süzmesi bu yüzden sunucuda değil burada.
  const { data: sozluk = [] } = useBirimler()

  if (!yeni && !kart) return <div className="crumb">Yükleniyor…</div>

  const tur: StokTuru = yeni ? (yeniTur ?? 'hammadde') : kart!.tur
  const hammadde = tur === 'hammadde'

  // Tür kısıtlı birim yalnız kendi türünde önerilir; kısıtsızlar her yerde.
  const birimler = birimSecenekleri(
    sozluk.filter((b) => !b.tur || b.tur === tur).map((b) => b.kod),
    [taslak.birim, kart?.birim],
  )

  async function tesisDegeriYaz(alan: string, ham: string) {
    if (!kart) return
    const temiz = ham.trim().replace(/\./g, '').replace(',', '.')
    const deger = temiz === '' ? null : Number(temiz)
    if (deger !== null && Number.isNaN(deger)) {
      toast('Geçersiz sayı — tesis değeri yazılmadı')
      return
    }
    try {
      await stokKartlari.tesisDegerleri(kart.id, { [alan]: deger })
      toast(
        deger === null
          ? `${kart.kod} · tesis değeri kaldırıldı, literatüre dönüldü`
          : `${kart.kod} · tesis değeri ${tr(deger, 2)} yazıldı (literatürü ezer)`,
      )
    } catch {
      toast('Tesis değeri kaydedilemedi')
    }
  }

  const baslikAksiyonlari = (
    <div className="page-actions">
      {!yeni && (
        <>
          <button
            className="btn btn-sec btn-sm"
            type="button"
            onClick={() =>
              ac({
                baslik: 'Stok kartını kopyala',
                altBaslik: `${kart!.kod} · ${kart!.ad}`,
                boyut: 'sm',
                icerik: <KopyaDrawer kart={kart!} />,
              })
            }
          >
            Kopyala
          </button>
          <button className="btn btn-danger btn-sm" type="button" onClick={() => toast('Pasife alma Faz 2’de')}>
            Pasife al
          </button>
        </>
      )}
      <button
        className="btn btn-sec btn-sm"
        type="button"
        disabled={yeni}
        onClick={() => {
          if (!kart) return
          setTaslak({
            ad: kart.ad,
            grup: kart.grup ?? '',
            birim: kart.birim ?? '',
            kritik: trGiris(kart.kritikSeviye),
          })
          toast('Değişiklikler geri alındı')
        }}
      >
        Vazgeç
      </button>
      <button
        className="btn btn-pri btn-sm"
        type="button"
        // Yeni kart akışı henüz yok: kod üretimi, 27 alanın tamamı ve tür
        // seçimi ayrı bir tasarım istiyor. Düğme yalan söylemek yerine kapalı.
        disabled={yeni || kaydet.isPending}
        title={yeni ? 'Yeni kart oluşturma akışı henüz bağlanmadı' : undefined}
        onClick={() => kaydet.mutate()}
      >
        Kaydet
      </button>
    </div>
  )

  // --- hammadde dışı: kısa form ---
  if (!hammadde && !yeni) {
    return (
      <>
        <div className="crumb">
          Ana Veri / Stok Kartları <b>/ {kart!.ad}</b>
        </div>
        <div className="d-head">
          <h1 className="d-title">{kart!.ad}</h1>
          <span className="d-kod">{kart!.kod}</span>
          <span className="tur-t">{TUR_ADI[tur]}</span>
          {baslikAksiyonlari}
        </div>
        <div className="d-grid">
          <div className="card">
            <div className="sect-t">Genel Bilgiler</div>
            <div className="form-g">
              <div className="fld"><label>Kart kodu</label><input className="in mono" defaultValue={kart!.kod} readOnly /></div>
              <div className="fld">
                <label>Ad</label>
                <input
                  className="in"
                  value={taslak.ad}
                  onChange={(e) => setTaslak((t) => ({ ...t, ad: e.target.value }))}
                />
              </div>
              <div className="fld">
                <label>Grup</label>
                <select
                  className="in"
                  value={taslak.grup}
                  onChange={(e) => setTaslak((t) => ({ ...t, grup: e.target.value }))}
                >
                  {[kart!.grup ?? '', ...GRUPLAR[tur].filter((g) => g !== kart!.grup)].map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label>Birim</label>
                {/*
                  Serbest metindi: aynı birim "t", "ton", "TON" diye üç kez
                  yazılabiliyor ve kart bazında toplama güvenilemiyordu.
                  Sözlük süper adminin kütüphanesinden geliyor; kartın MEVCUT
                  birimi listede yoksa yine de eklenir (birimSecenekleri) —
                  aksi hâlde kaydetmek onu sessizce başka bir değere çevirirdi.
                */}
                <select
                  className="in"
                  value={taslak.birim}
                  onChange={(e) => setTaslak((t) => ({ ...t, birim: e.target.value }))}
                >
                  {birimler.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label>Kritik seviye</label>
                <input
                  className="in mono"
                  value={taslak.kritik}
                  onChange={(e) => setTaslak((t) => ({ ...t, kritik: e.target.value }))}
                />
              </div>
              <div className="fld">
                <label>Tedarikçi</label>
                <div className="with-plus">
                  <select className="in js-ted">
                    {TEDARIKCILER.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <button
                    className="plus-btn"
                    type="button"
                    title="İşlemi bozmadan yeni cari ekle"
                    onClick={() =>
                      ac({
                        baslik: 'Yeni cari — hızlı kayıt',
                        altBaslik: 'Mevcut işleminiz açık kalır',
                        boyut: 'sm',
                        icerik: <HizliCariDrawer />,
                      })
                    }
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="sect-t">Son Hareketler</div>
            <div className="mini-list" style={{ padding: '4px 0' }}>
              <div className="mini-li" style={{ padding: '6px 12px' }}>
                Hareket defteri Faz 2’de bağlanacak
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // --- hammadde (ve yeni kart) ---
  const kul = kart?.okmKm == null ? null : 100 - kart.okmKm

  // Referans sırası: önce kartın mevcut kategorisi, sonra kütüphane, en sonda
  // bu oturumda hızlı kayıtla eklenenler. Yinelenen ad listeye iki kez girmez.
  const kategoriSecenekleri = [
    ...(yeni ? [] : [kart!.grup ?? '']),
    ...GRUPLAR[tur].filter((g) => yeni || g !== kart!.grup),
    ...ekKategoriler,
  ].filter((g, i, hepsi) => g !== '' && hepsi.indexOf(g) === i)

  return (
    <>
      <div className="crumb">
        Ana Veri / Stok Kartları <b>/ {yeni ? 'Yeni Kart' : kart!.ad}</b>
      </div>

      <div className="d-head">
        <h1 className="d-title">{yeni ? 'Yeni Stok Kartı' : kart!.ad}</h1>
        {!yeni && <span className="d-kod">{kart!.kod}</span>}
        {!yeni && kart!.hij && (
          <span className="badge b-amber"><span className="dot" />Hijyenizasyon gerekli</span>
        )}
        {baslikAksiyonlari}
      </div>

      <div className="d-grid">
        <div>
          <div className="card">
            <div className="sect-t">Genel Bilgiler</div>
            <div className="form-g">
              <div className="fld">
                <label>Kart kodu</label>
                <input className="in mono" value={yeni ? (sonrakiKod?.kod ?? '…') : kart!.kod} readOnly />
              </div>
              <div className="fld">
                <label>Substrat adı</label>
                {/* Kontrollü: alan `defaultValue` ile duruyordu ve Kaydet
                    `taslak.ad`'ı gönderiyordu — hammadde kartında adı
                    değiştirmek hiçbir yere yazılmıyor, toast yine
                    "kaydedildi" diyordu. Kısa formda düzeltilmiş, burası
                    atlanmıştı. */}
                <input
                  className="in"
                  value={taslak.ad}
                  placeholder="Örn. Mısır Silajı"
                  onChange={(e) => setTaslak((t) => ({ ...t, ad: e.target.value }))}
                />
              </div>
              <div className="fld">
                <label>Kategori</label>
                <div className="with-plus">
                  {/* Kontrollü: alan `defaultValue` ile duruyordu, yani hammadde
                      kartında kategori değiştirmek hiçbir yere yazılmıyordu —
                      Kaydet eski grubu geri gönderiyordu. */}
                  <select
                    className="in js-kat"
                    value={taslak.grup}
                    onChange={(e) => setTaslak((t) => ({ ...t, grup: e.target.value }))}
                  >
                    {/* Referans: mevcut grup (yeni kartta "Seçin…") başa alınır. */}
                    {(yeni || !taslak.grup) && <option value="">Seçin…</option>}
                    {kategoriSecenekleri.map((g) => <option key={g}>{g}</option>)}
                  </select>
                  <button
                    className="plus-btn"
                    type="button"
                    title="İşlemi bozmadan yeni kategori ekle"
                    onClick={() =>
                      ac({
                        baslik: 'Yeni kategori — hızlı kayıt',
                        altBaslik: 'Mevcut işleminiz açık kalır',
                        boyut: 'sm',
                        icerik: (
                          <HizliKategoriDrawer
                            mevcutlar={kategoriSecenekleri}
                            onSec={(yeniAd) => {
                              setEkKategoriler((k) => [...k, yeniAd])
                              setTaslak((t) => ({ ...t, grup: yeniAd }))
                              toast(
                                `"${yeniAd}" kategorisi eklendi ve seçildi — kart kaydedilince yazılır`,
                              )
                            }}
                          />
                        ),
                      })
                    }
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="fld">
                <label>Birim</label>
                {/* Hammadde formunda birim alanı hiç yoktu: substrat ton
                    üzerinden işleniyor varsayılmıştı. Ama `stok_karti.birim`
                    hammadde kartında da dolu ve kısa formdan düzenlenebiliyor
                    — alan burada olmayınca kullanıcı kendi kartının birimini
                    göremiyordu. Seçenekler sözlükten (bkz. lib/birimler). */}
                <select
                  className="in"
                  value={taslak.birim}
                  onChange={(e) => setTaslak((t) => ({ ...t, birim: e.target.value }))}
                >
                  {birimler.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label>Kaynak</label>
                <input
                  className="in"
                  readOnly
                  value={
                    yeni
                      ? 'Özel'
                      : kart!.kaynak === 'literatur'
                        ? 'Literatür (KTBL / VDI 4630)'
                        : kart!.kaynak === 'sistem'
                          ? 'Sistem'
                          : 'Özel'
                  }
                />
              </div>
              <div className="fld">
                <label>Hijyenizasyon</label>
                <div className="switch">
                  <span className={!yeni && kart!.hij ? 'sw on' : 'sw'} />
                  {!yeni && kart!.hij ? 'Gerekli (K19)' : 'Gerekli değil'}
                </div>
              </div>
              <div className="fld">
                <label>Durum</label>
                <div className="switch">
                  <span className={yeni || kart!.durum === 'aktif' ? 'sw on' : 'sw'} />
                  {yeni || kart!.durum === 'aktif' ? 'Aktif' : 'Pasif'}
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 10 }}>
            <div className="sect-t">Biyogaz Parametreleri — Değer Önceliği</div>
            <table className="ptab">
              <thead>
                <tr>
                  <th>Parametre</th>
                  <th className="num" style={{ width: 100 }}>Literatür</th>
                  <th style={{ width: 106 }}>Tesis</th>
                  <th style={{ width: 142 }}>Etkin Değer</th>
                  <th style={{ width: 100 }}>Birim</th>
                </tr>
              </thead>
              <tbody>
                <OncelikSatiri
                  etiket="Kuru madde (KM)" birim="% YM" yeni={yeni}
                  literatur={kart?.km ?? null}
                  tesis={kart?.tesisDegerleri?.km ?? null}
                  etkin={teorik?.km ?? null}
                  onTesisDegisti={(v) => void tesisDegeriYaz('km', v)}
                />
                <OncelikSatiri
                  etiket="Organik kuru madde (OKM)" birim="% KM" yeni={yeni}
                  literatur={kart?.okmKm ?? null}
                  tesis={kart?.tesisDegerleri?.okm_km ?? null}
                  etkin={teorik?.okmYuzde ?? null}
                  onTesisDegisti={(v) => void tesisDegeriYaz('okm_km', v)}
                />
                {/* Kül hesaplanan satır: 100 − OKM. Tesis kolonu yok. */}
                <tr>
                  <td>Kül</td>
                  <td className="num calc">hesaplanan</td>
                  <td />
                  <td>
                    <span className="eff">
                      {yeni || kul === null ? '—' : tr(kul, 2)}
                      <span className="eff-src">= 100 − OKM</span>
                    </span>
                  </td>
                  <td className="num" style={{ color: '#8A98AB' }}>% KM</td>
                </tr>
                <OncelikSatiri
                  etiket="Özgül biyogaz verimi" birim="Nm³/t oKM" yeni={yeni}
                  literatur={kart?.verimNlKgOkm ?? null}
                  tesis={kart?.tesisDegerleri?.verim_nl_kg_okm ?? null}
                  etkin={teorik?.verim ?? null}
                  onTesisDegisti={(v) => void tesisDegeriYaz('verim_nl_kg_okm', v)}
                />
                <OncelikSatiri
                  etiket="Metan oranı (CH₄)" birim="%" yeni={yeni}
                  literatur={kart?.ch4Oran ?? null}
                  tesis={kart?.tesisDegerleri?.ch4_oran ?? null}
                  etkin={teorik?.ch4 ?? null}
                  onTesisDegisti={(v) => void tesisDegeriYaz('ch4_oran', v)}
                />
              </tbody>
            </table>
            <div className="pnote">
              Etkin değer önceliği: <b>lot analizi → tesis değeri → literatür</b> (K13).
            </div>
          </div>

          <BiyogazParametreleri kart={kart ?? null} yeni={yeni} />
        </div>

        <div>
          <div className="side-card" style={{ marginTop: 0 }}>
            <div className="sect-t">Son Analizler · KM %</div>
            <div className="mini-list" style={{ padding: '4px 0' }}>
              <div className="mini-li" style={{ padding: '6px 12px' }}>
                Laboratuvar kayıtları Faz 2’de bağlanacak
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
